/**
 * One attention record for a prompt spoken over several live-voice segments.
 *
 * Each segment records its own marks and screen recording. A prompt made of
 * several keeps them all: the earliest segment is the base, the later ones'
 * times move onto its clock, and their chapter and stroke ids are prefixed
 * with their recording id, since chapter ids repeat from one recording to the
 * next. Every recording id stays listed, so all clips and chapter captures
 * are attached, then accepted or cleared together.
 */
import { clearVoiceAttentionSessionEverywhere } from "./voice-attention-session";
import {
  awaitVoiceAttentionChapterCaptures,
  type VoiceAttentionChapterCapture,
} from "./voice-attention-chapter-capture";
import type {
  VoiceAttentionChapter,
  VoiceAttentionState,
} from "./voice-attention-store";

/** The recordings a prompt's attention covers: its own and any merged in. */
export function voiceAttentionRecordingIds(
  state: VoiceAttentionState,
): string[] {
  return [state.recordingId, ...(state.mergedRecordingIds ?? [])].filter(
    (id): id is string => id !== null,
  );
}

/** Clears every recording a prompt that was never sent had collected. */
export function clearMergedVoiceAttention(state: VoiceAttentionState): void {
  for (const recordingId of voiceAttentionRecordingIds(state)) {
    clearVoiceAttentionSessionEverywhere(recordingId);
  }
}

function mergedId(recordingId: string, id: string): string {
  return `${recordingId}/${id}`;
}

function shiftedChapter(
  chapter: VoiceAttentionChapter,
  recordingId: string,
  offsetMs: number,
): VoiceAttentionChapter {
  const shift = (ms: number | undefined) =>
    ms === undefined ? undefined : ms + offsetMs;
  return {
    ...chapter,
    id: mergedId(recordingId, chapter.id),
    openedMs: chapter.openedMs + offsetMs,
    settledMs: shift(chapter.settledMs),
    closedMs: shift(chapter.closedMs),
  };
}

/** Moves a later segment's attention onto the base segment's clock. */
function foldInto(
  base: VoiceAttentionState,
  later: VoiceAttentionState & { recordingId: string },
): VoiceAttentionState {
  const offsetMs = Math.max(
    0,
    Math.round(later.clockStartedAt - base.clockStartedAt),
  );
  const recordingId = later.recordingId;
  return {
    ...base,
    activation: base.activation ?? later.activation,
    mergedRecordingIds: [
      ...(base.mergedRecordingIds ?? []),
      ...voiceAttentionRecordingIds(later),
    ],
    chapters: [
      ...base.chapters,
      ...later.chapters.map((chapter) =>
        shiftedChapter(chapter, recordingId, offsetMs),
      ),
    ],
    strokes: [
      ...base.strokes,
      ...later.strokes.map((stroke) => ({
        ...stroke,
        id: mergedId(recordingId, stroke.id),
        startedMs: stroke.startedMs + offsetMs,
        endedMs:
          stroke.endedMs === undefined ? undefined : stroke.endedMs + offsetMs,
        points: stroke.points.map((point) => ({
          ...point,
          tMs: point.tMs + offsetMs,
          chapterId: mergedId(recordingId, point.chapterId),
        })),
      })),
    ],
    transcriptTimeline: [
      ...base.transcriptTimeline,
      ...later.transcriptTimeline.map((snapshot) => ({
        ...snapshot,
        tMs: snapshot.tMs + offsetMs,
        speechSegments: snapshot.speechSegments.map((segment) => ({
          ...segment,
          start_ms: segment.start_ms + offsetMs,
          end_ms: segment.end_ms + offsetMs,
        })),
      })),
    ],
  };
}

/** The segments' attention, oldest first, as one record. */
export function mergeVoiceAttentionStates(
  states: VoiceAttentionState[],
): VoiceAttentionState {
  const recorded = states.filter(
    (state): state is VoiceAttentionState & { recordingId: string } =>
      state.recordingId !== null,
  );
  const [base, ...later] = recorded;
  if (!base) return states[states.length - 1]!;
  return later.reduce<VoiceAttentionState>(foldInto, base);
}

/**
 * The chapter captures of every recording a prompt covers, with merged-in
 * chapters under the ids the merged attention gives them.
 */
export async function awaitMergedVoiceAttentionCaptures(
  state: VoiceAttentionState,
  timeoutMs: number,
): Promise<VoiceAttentionChapterCapture[]> {
  const chapterIds = state.chapters.map((chapter) => chapter.id);
  const own = awaitVoiceAttentionChapterCaptures(
    state.recordingId,
    timeoutMs,
    chapterIds,
  );
  const merged = (state.mergedRecordingIds ?? []).map(async (recordingId) => {
    const prefix = mergedId(recordingId, "");
    const captures = await awaitVoiceAttentionChapterCaptures(
      recordingId,
      timeoutMs,
      chapterIds
        .filter((id) => id.startsWith(prefix))
        .map((id) => id.slice(prefix.length)),
    );
    return captures.map((capture) => ({
      ...capture,
      chapterId: mergedId(recordingId, capture.chapterId),
    }));
  });
  return (await Promise.all([own, ...merged])).flat();
}
